'use client';

import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

// WHY: hand-rolled, not CSS `overflow-anchor`: Safari/iOS WebKit ships no support and there is no polyfill
export function useScrollAnchor(
  scrollRef: RefObject<HTMLElement | null>,
  signature: string,
): void {
  const anchor = useRef<{ el: HTMLElement; top: number } | null>(null);

  const record = useCallback(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const base = sc.getBoundingClientRect().top;
    for (const el of sc.querySelectorAll<HTMLElement>('[data-anchor-id]')) {
      const top = el.getBoundingClientRect().top - base;
      if (top >= -1) {
        anchor.current = { el, top };
        return;
      }
    }
    anchor.current = null;
  }, [scrollRef]);

  useLayoutEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const prev = anchor.current;
    if (prev && prev.el.isConnected && sc.contains(prev.el)) {
      const now = prev.el.getBoundingClientRect().top - sc.getBoundingClientRect().top;
      const delta = now - prev.top;
      if (Math.abs(delta) > 0.5) sc.scrollTop += delta;
    }
    record();
  }, [signature, record, scrollRef]);

  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    record();
    const onScroll = () => record();
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => sc.removeEventListener('scroll', onScroll);
  }, [record, scrollRef]);
}
